import { useEffect, useState } from "react";
import type {
  ComparisonDetail,
  ComparisonRelation,
} from "../../../shared/contracts/product-pages.js";
import { loadProductPage } from "../../api/product-page-api.js";
import type { RivalInsight } from "../../../shared/presentation/rival-insight.js";
import { useComparisonRelations } from "../rivalry/comparison-api.js";
import { useRivalryCalendar } from "../rivalry/use-rivalry-calendar.js";
import {
  loadClubhouseSections,
  type ClubhouseSections,
} from "./clubhouse-section-loader.js";

export async function loadComparisons(): Promise<ComparisonRelation[]> {
  const result = await comparisonJson<{
    definitionVersion: 1;
    relations: ComparisonRelation[];
  }>("/api/v1/rivalries");
  return result.relations;
}

export function loadComparison(
  id: string,
  query = "basis=gross",
): Promise<ComparisonDetail> {
  return comparisonJson(`/api/v1/rivalries/${encodeURIComponent(id)}?${query}`);
}

async function comparisonJson<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    credentials: "include",
    headers: { accept: "application/json" },
  });
  if (!response.ok) throw new Error("Your shared history could not load.");
  return response.json();
}

interface ClubhouseHistory extends ClubhouseSections {
  relations: ComparisonRelation[];
}
const empty: ClubhouseHistory = {
  loading: true,
  error: null,
  relations: [],
  rivals: [],
  rounds: [],
  featured: null,
  finding: null,
  insightLoading: true,
};

/** No placeholder records: failed evidence loads stay explicitly unavailable. */
export function useClubhouseHistory(revision: string) {
  const calendar = useRivalryCalendar();
  const relations = useComparisonRelations(revision, calendar.filters);
  const [history, setHistory] = useState<ClubhouseHistory>(empty);
  useEffect(() => {
    let current = true;
    setHistory(empty);
    void loadClubhouseSections({
      rivals: () => loadProductPage("rivals"),
      rounds: () => loadProductPage("stats"),
      insight: () =>
        comparisonJson<{
          finding: RivalInsight;
          detail: ComparisonDetail;
        } | null>("/api/v1/feed/insight"),
      publish: (patch) => {
        if (current) setHistory((previous) => ({ ...previous, ...patch }));
      },
    });
    return () => {
      current = false;
    };
  }, [revision]);
  return {
    ...history,
    relations: relations.data?.relations ?? [],
    relationsLoading: relations.loading,
    relationsError: relations.error,
    availableYears: relations.catalog?.availableYears ?? [],
    hasSimulator: relations.catalog?.hasSimulator ?? false,
  };
}
